import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, Pencil, Plus, ShieldCheck, Trash2, UserRoundCheck, Users } from 'lucide-react';
import MainLayout from '../components/layout/MainLayout';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { kategorienApi, permissionsApi, tagsApi, usersApi } from '../lib/api';

const FARBEN = ['#64748b', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6'];
const EMPTY_FORM = {
  name: '',
  farbe: FARBEN[0],
  beschreibung: '',
  permission_keys: [],
  linked_tag_id: null,
  hauptverantwortlicher_user_id: null,
  ist_aktiv: true,
  ist_sachgebiet: false,
};

export default function MitarbeiterKategorienPage() {
  const [items, setItems] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [tags, setTags] = useState([]);
  const [mitarbeiter, setMitarbeiter] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dialog, setDialog] = useState({ open: false, entry: null });
  const [form, setForm] = useState(EMPTY_FORM);

  const load = async () => {
    setLoading(true);
    try {
      const [groupsResult, permissionsResult, tagsResult, usersResult] = await Promise.all([
        kategorienApi.list(),
        permissionsApi.getAvailable(),
        tagsApi.getAll(),
        usersApi.getAll({ ist_aktiv: true }),
      ]);
      setItems(groupsResult.data || []);
      setPermissions(permissionsResult.data?.group_assignable_permissions || []);
      setTags((tagsResult.data || []).filter((tag) => tag.ist_aktiv !== false));
      setMitarbeiter(
        (usersResult.data || [])
          .filter((user) => user.ist_aktiv !== false && user.ist_system_user !== true)
          .sort((a, b) => (
            `${a.nachname || ''} ${a.vorname || ''}`
              .localeCompare(`${b.nachname || ''} ${b.vorname || ''}`, 'de')
          ))
      );
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Benutzergruppen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setForm({ ...EMPTY_FORM, permission_keys: [] });
    setDialog({ open: true, entry: null });
  };

  const openEdit = (entry) => {
    setForm({
      name: entry.name || '',
      farbe: entry.farbe || FARBEN[0],
      beschreibung: entry.beschreibung || '',
      permission_keys: entry.permission_keys || [],
      linked_tag_id: entry.linked_tag_id || null,
      hauptverantwortlicher_user_id: entry.hauptverantwortlicher_user_id || null,
      ist_aktiv: entry.ist_aktiv !== false,
      ist_sachgebiet: entry.ist_sachgebiet === true,
    });
    setDialog({ open: true, entry });
  };

  const togglePermission = (key) => {
    setForm((current) => ({
      ...current,
      permission_keys: current.permission_keys.includes(key)
        ? current.permission_keys.filter((item) => item !== key)
        : [...current.permission_keys, key],
    }));
  };

  const save = async () => {
    if (!form.name.trim()) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }
    if (!form.hauptverantwortlicher_user_id) {
      toast.error('Bitte einen Hauptverantwortlichen auswählen');
      return;
    }
    setSaving(true);
    try {
      if (dialog.entry) {
        await kategorienApi.update(dialog.entry.id, form);
      } else {
        await kategorienApi.create(form);
      }
      toast.success(`Benutzergruppe „${form.name}“ gespeichert`);
      setDialog({ open: false, entry: null });
      await load();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : detail?.message || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const deactivate = async (entry) => {
    if (!window.confirm(`Benutzergruppe „${entry.name}“ deaktivieren? Bestehende Zuordnungen und Historien bleiben erhalten.`)) return;
    try {
      await kategorienApi.delete(entry.id);
      toast.success('Benutzergruppe deaktiviert');
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Deaktivieren fehlgeschlagen');
    }
  };

  const permissionLabel = (key) => permissions.find((item) => item.key === key)?.label || key;
  const userLabel = (userId) => {
    const user = mitarbeiter.find((entry) => entry.id === userId);
    return user ? `${user.nachname}, ${user.vorname}` : null;
  };

  return (
    <MainLayout>
      <div className="max-w-5xl mx-auto space-y-4 p-4">
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5" /> Benutzergruppen</CardTitle>
              <CardDescription>
                Zusätzliche Fachgruppen pro Wache. Rechte stammen aus der Gruppe; ein verknüpfter Tag dient nur der sichtbaren Kennzeichnung.
              </CardDescription>
            </div>
            <Button onClick={openCreate} data-testid="gruppe-add-btn">
              <Plus className="w-4 h-4 mr-1" /> Neue Benutzergruppe
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="py-12 text-center"><Loader2 className="w-6 h-6 mx-auto animate-spin" /></div>
            ) : items.length === 0 ? (
              <p className="py-8 text-center text-sm text-muted-foreground">Noch keine Benutzergruppen angelegt.</p>
            ) : (
              <div className="grid gap-3 md:grid-cols-2">
                {items.map((entry) => (
                  <div key={entry.id} className="border rounded-lg p-4 space-y-3" data-testid={`gruppe-item-${entry.id}`}>
                    <div className="flex gap-3">
                      <span className="w-3 rounded shrink-0" style={{ backgroundColor: entry.farbe }} />
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold">{entry.name}</p>
                        {entry.beschreibung && <p className="text-xs text-muted-foreground">{entry.beschreibung}</p>}
                      </div>
                      <Button size="icon" variant="ghost" onClick={() => openEdit(entry)}><Pencil className="w-4 h-4" /></Button>
                      <Button size="icon" variant="ghost" className="text-destructive" onClick={() => deactivate(entry)}><Trash2 className="w-4 h-4" /></Button>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Users className="w-3.5 h-3.5" />
                      {entry.aktive_mitarbeiter_anzahl || 0} aktive Mitarbeiter
                    </div>
                    <div className="flex items-center gap-2 text-sm">
                      <UserRoundCheck className="w-4 h-4 text-primary" />
                      <span className="text-muted-foreground">Hauptverantwortlich:</span>
                      <span className="font-medium">
                        {userLabel(entry.hauptverantwortlicher_user_id) || 'Noch nicht festgelegt'}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {(entry.permission_keys || []).length === 0
                        ? <Badge variant="outline">Keine zusätzlichen Rechte</Badge>
                        : entry.permission_keys.map((key) => <Badge key={key} variant="secondary">{permissionLabel(key)}</Badge>)}
                      {entry.ist_sachgebiet && <Badge>Als Sachgebiet sichtbar</Badge>}
                    </div>
                    {entry.linked_tag_id && (
                      <p className="text-xs text-muted-foreground">
                        Sichtbarer Tag: {tags.find((tag) => tag.id === entry.linked_tag_id)?.name || 'nicht mehr vorhanden'}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialog.open} onOpenChange={(open) => !open && setDialog({ open: false, entry: null })}>
          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{dialog.entry ? 'Benutzergruppe bearbeiten' : 'Neue Benutzergruppe'}</DialogTitle></DialogHeader>
            <div className="space-y-5">
              <div><Label>Name</Label><Input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} maxLength={60} /></div>
              <div><Label>Beschreibung</Label><Textarea value={form.beschreibung} onChange={(event) => setForm({ ...form, beschreibung: event.target.value })} maxLength={500} /></div>
              <div>
                <Label>Hauptverantwortlicher *</Label>
                <select
                  className="w-full mt-1 border rounded-md bg-background px-3 py-2 text-sm"
                  value={form.hauptverantwortlicher_user_id || ''}
                  onChange={(event) => setForm({
                    ...form,
                    hauptverantwortlicher_user_id: event.target.value || null,
                  })}
                  data-testid="gruppe-hauptverantwortlicher"
                >
                  <option value="">Bitte Mitarbeiter auswählen</option>
                  {mitarbeiter.map((user) => (
                    <option key={user.id} value={user.id}>
                      {user.nachname}, {user.vorname}
                      {user.dienstgrad ? ` · ${user.dienstgrad}` : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-muted-foreground mt-1">
                  Die Person muss ein aktiver Mitarbeiter dieser Wache sein.
                </p>
              </div>
              <div>
                <Label>Farbe</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {FARBEN.map((color) => <button key={color} type="button" aria-label={`Farbe ${color}`} onClick={() => setForm({ ...form, farbe: color })} className={`w-8 h-8 rounded-full border-2 ${form.farbe === color ? 'border-foreground scale-110' : 'border-transparent'}`} style={{ backgroundColor: color }} />)}
                </div>
              </div>
              <div>
                <Label className="flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> Zusätzliche Rechte</Label>
                <p className="text-xs text-muted-foreground mb-2">Kritische System- und Rollenrechte werden hier bewusst nicht angeboten.</p>
                <div className="grid gap-2 sm:grid-cols-2">
                  {permissions.map((permission) => (
                    <label key={permission.key} className="flex gap-2 border rounded-md p-3 cursor-pointer">
                      <input type="checkbox" checked={form.permission_keys.includes(permission.key)} onChange={() => togglePermission(permission.key)} />
                      <span><span className="block text-sm font-medium">{permission.label}</span><span className="block text-xs text-muted-foreground">{permission.beschreibung}</span></span>
                    </label>
                  ))}
                </div>
              </div>
              <div>
                <Label>Optionaler sichtbarer Tag</Label>
                <select className="w-full mt-1 border rounded-md bg-background px-3 py-2 text-sm" value={form.linked_tag_id || ''} onChange={(event) => setForm({ ...form, linked_tag_id: event.target.value || null })}>
                  <option value="">Kein Tag verknüpft</option>
                  {tags.map((tag) => <option key={tag.id} value={tag.id}>{tag.name}</option>)}
                </select>
                <p className="text-xs text-muted-foreground mt-1">Die Verknüpfung zeigt den Tag an. Der Tag selbst vergibt niemals Rechte.</p>
              </div>
              <label className="flex items-start gap-3 rounded-lg border p-3 cursor-pointer">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={form.ist_sachgebiet}
                  onChange={(event) => setForm({ ...form, ist_sachgebiet: event.target.checked })}
                  data-testid="gruppe-ist-sachgebiet"
                />
                <span>
                  <span className="block text-sm font-medium">Als Sachgebiet anzeigen</span>
                  <span className="block text-xs text-muted-foreground">
                    Die Gruppe erscheint unter Mitarbeiter → Sachgebiete und zeigt dort ihre zugeordneten aktiven Mitarbeiter.
                  </span>
                </span>
              </label>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setDialog({ open: false, entry: null })}>Abbrechen</Button>
              <Button onClick={save} disabled={saving}>{saving && <Loader2 className="w-4 h-4 mr-1 animate-spin" />} Speichern</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </MainLayout>
  );
}
